import { Modal } from '../../UI/Modal/Modal'
import { z } from 'zod'
import apiService from '../../../services/api'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import to from 'await-to-js'
import { toast } from 'react-toastify'
import { TextInput } from '../../FormUtils/TextInput'
import Select, { SelectOption } from '../../FormUtils/Select'
import { PhoneInput } from '../../FormUtils/PhoneInput'
import CreateDealModal from '../../Deals/CreateDealModal/CreateDealModal'
import { useQueryClient } from '@tanstack/react-query'
import { US_STATES } from 'utils/us_states'
import { US_CITIES } from 'utils/us_cities'
import { AxiosError } from 'axios'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import { ALL_COUNTRIES } from 'utils/countries'
import {
  extensionZodValidator,
  phoneNumberZodValidor,
} from 'utils/zodValidators'
import { useDebounce } from 'use-debounce'
import { BsCheck } from 'react-icons/bs'
import { Spinner } from 'react-bootstrap'
import { ErrorLabel } from 'components/FormUtils/ErrorLabel'

// Domain regex: https://stackoverflow.com/questions/10306690/what-is-a-regular-expression-which-will-match-a-valid-domain-name-without-a-subd
const domainRegex =
  /^(((?!-))(xn--|_)?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/

const companyFormShapeItems = {
  id: z.number().optional(),
  name: z.string().refine((val) => val.length > 0, {
    message: 'Company name is required',
  }),
  domain: z
    .string()
    .trim()
    .optional()
    .nullable()
    // Allow empty string initially, validation moved to superRefine
    .refine(
      (v) => {
        if (!v) return true // Allow empty
        // Test if domain is valid format and has at least one dot
        return domainRegex.test(v) && v.includes('.')
      },
      {
        message: 'Enter domain as: domain.com',
      }
    ),
  unique_domain: z.boolean(),
  country: z
    .string({ invalid_type_error: 'Please select a country' })
    .default('US'),
  // Allow empty string initially, validation moved to superRefine
  address: z.string().optional().nullable(),
  city: z.string().min(1),
  state: z
    .string({
      invalid_type_error: 'Please select a state',
    })
    .optional(),
  // Allow empty string initially, validation moved to superRefine
  zipcode: z.string().optional().nullable(),
  extension: extensionZodValidator,
  phone_number: phoneNumberZodValidor,
  distributors: z.array(z.number()).optional(),
  taglist: z.array(z.number()).optional(),
  category: z.number({
    required_error: 'Please select a category to continue.',
    invalid_type_error: 'Please select a category to continue.',
  }),
  sub_category: z.number({
    required_error: 'Please select a type to continue.',
    invalid_type_error: 'Please select a type to continue.',
  }),
  google_place_url: z
    .string()
    .url({
      message:
        'Enter a valid URL: https://google.com/maps... or https://maps.app.goo.gl/...',
    })
    .optional()
    .refine(
      (val) => {
        if (!val) return true
        return (
          val.includes('google.com/maps') || val.includes('maps.app.goo.gl')
        )
      },
      {
        message:
          'Must be a valid Google Maps URL, e.g. https://google.com/maps... or https://maps.app.goo.gl/...',
      }
    )
    .or(z.literal('')),
}

// Base type derived from the shape items
type BaseCompanyFormShape = z.infer<z.ZodObject<typeof companyFormShapeItems>>

// Final type will be inferred from the schema defined inside the component
export type CompanyFormShape = BaseCompanyFormShape

export function CompanyModal({
  show,
  setShow,
  title,
  description,
  acceptButtonText,
  defaultValues,
  disableFields,
  hideCategoryAndType,
  hideKnownDistributorsAndTags,
  skipDealCreation,
  apiAction,
  type = 'chain',
  checkUniqueDomain = false,
}: {
  show: boolean
  setShow: (show: boolean) => void
  title: string
  description: string
  acceptButtonText: string
  defaultValues?: Partial<CompanyFormShape>
  disableFields?: (keyof CompanyFormShape)[]
  hideCategoryAndType?: boolean
  hideKnownDistributorsAndTags?: boolean
  skipDealCreation?: boolean
  apiAction: (
    values: CompanyFormShape
  ) => Promise<Record<string, any> | undefined>
  type?: 'chain' | 'door'
  checkUniqueDomain?: boolean
}) {
  const contextTexts = {
    chain: {
      nameLabel: 'Company',
      namePlaceholder: 'Enter company name',
      domainLabel: 'Company Domain',
      addressHelperText: '',
    },
    door: {
      nameLabel: 'Door',
      namePlaceholder: 'Enter door name',
      domainLabel: 'Door Domain',
      addressHelperText: '',
    },
  }

  const texts = contextTexts[type]

  const api = apiService()

  const queryClient = useQueryClient()

  const { data: categories } = api.useGetCompanyCategories()
  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()

  const companyCategories = useMemo(() => categories || [], [categories])

  // Define the Zod schema dynamically inside the component using useMemo
  const companyFormZod = useMemo(() => {
    // Find IDs for conditional validation
    const intermediariesCategory = companyCategories?.find(
      (c) => c.name === 'Intermediaries'
    )
    const otherSubCategory = intermediariesCategory?.sub_categories.find(
      (s) => s.name === 'Other'
    )

    return z
      .object({
        ...companyFormShapeItems,
        // Override category/sub_category based on hideCategoryAndType prop
        category: hideCategoryAndType
          ? z.number().optional()
          : companyFormShapeItems.category, // Use original if not hidden
        sub_category: hideCategoryAndType
          ? z.number().optional()
          : companyFormShapeItems.sub_category, // Use original if not hidden
      })
      .superRefine((val, ctx) => {
        console.log('Validation started')
        // Existing US State validation
        if (val.country === 'US' && !val.state) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom, // Use custom to avoid automatic minLength check
            message: 'State is required for US',
            path: ['state'],
          })
        }

        // Check if the special condition (Intermediaries -> Other) is met
        const isIntermediariesOther =
          val.category === intermediariesCategory?.id &&
          val.sub_category === otherSubCategory?.id

        // Apply original validation for address and zipcode if condition is NOT met
        if (!isIntermediariesOther) {
          // Validate Address: required
          if (!val.address || val.address.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Address is required',
              path: ['address'],
            })
          }

          // Validate Zipcode: required
          if (!val.zipcode || val.zipcode.trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'ZIP/Postal code is required',
              path: ['zipcode'],
            })
          }
        }
      })
  }, [companyCategories, hideCategoryAndType]) // Depend on categories and prop

  const categoryOptions = useMemo(
    () =>
      companyCategories?.map((category) => ({
        label: category.name,
        value: category.id,
      })) || [],
    [companyCategories]
  )

  const {
    control,
    handleSubmit,
    formState,
    watch,
    reset,
    setError,
    clearErrors,
    setValue,
  } = useForm<CompanyFormShape>({
    mode: 'all',
    defaultValues: {
      country: 'US',
      unique_domain: !checkUniqueDomain, // Initialize based on prop
      ...defaultValues,
    },
    // Use the memoized dynamic Zod schema
    resolver: zodResolver(companyFormZod),
  })

  useEffect(() => {
    // Reset unique_domain along with other defaults when defaultValues change
    reset({
      country: 'US',
      unique_domain: !checkUniqueDomain,
      ...defaultValues,
    })
  }, [defaultValues, reset, checkUniqueDomain])

  const watchCountry = watch('country')
  const watchCategory = watch('category')
  const watchSubCategory = watch('sub_category')

  // Validate Domain Uniqueness
  const watchDomain = watch('domain')
  const watchUniqueDomain = watch('unique_domain') // This field tracks if the domain is confirmed unique
  const [dDomain] = useDebounce(watchDomain, 500)
  const controllerRef = useRef<AbortController | null>(null)
  const [checkingDomain, setCheckingDomain] = useState(false)

  // Domain is now always optional for all categories
  const isDomainRequired = useMemo(() => {
    return false
  }, [])

  async function checkDomainUnique(
    domain: string,
    signal: AbortController['signal']
  ) {
    setCheckingDomain(true)
    const [err, isNotUnique] = await to(
      api.searchCompaniesUniqueDomain(domain, signal)
    )

    // Handle aborted request or other errors gracefully
    if (err && (err as any).name === 'AbortError') {
      console.log('Domain check aborted')
      setCheckingDomain(false)
      return // Don't update state if aborted
    } else if (err) {
      console.error('Error checking domain uniqueness:', err)
      // Optionally show a generic error to the user
      // toast.error("Could not verify domain uniqueness.");
      setCheckingDomain(false)
      // Potentially set unique_domain to false to block submission?
      setValue('unique_domain', false, { shouldValidate: true })
      setError('domain', {
        type: 'manual',
        message: 'Could not verify domain',
      })
      return
    }

    // If the API call was successful
    if (isNotUnique) {
      setValue('unique_domain', false, { shouldValidate: true })
      // Only set error if the domain is actually required
      if (isDomainRequired) {
        setError('domain', { type: 'manual', message: 'Domain in use' })
      } else {
        // If domain is not required but entered and not unique, clear potential format error but keep unique_domain false
        clearErrors('domain')
      }
    } else {
      setValue('unique_domain', true, { shouldValidate: true })
      clearErrors('domain') // Clear any previous errors (format or uniqueness)
    }

    setCheckingDomain(false)
  }

  useEffect(() => {
    // Skip check if the feature is disabled
    if (!checkUniqueDomain) {
      // Ensure unique_domain is true if check is disabled, allowing submission
      if (!watchUniqueDomain) {
        setValue('unique_domain', true, { shouldValidate: false }) // No need to re-validate schema here
      }
      return
    }

    // Abort previous request if a new one is starting
    if (controllerRef.current) {
      controllerRef.current.abort()
    }
    const newController = new AbortController()
    controllerRef.current = newController
    setCheckingDomain(false) // Reset checking state

    // Conditions to run the check:
    // 1. Debounced domain (dDomain) has a value.
    // 2. The domain format is valid according to the base Zod schema (no existing format errors).
    const domainFormatError =
      formState.errors.domain?.message?.includes('domain.com')
    const domainRequiredError =
      formState.errors.domain?.message?.includes('required')

    if (
      dDomain &&
      dDomain.trim().length > 0 &&
      !domainFormatError &&
      !domainRequiredError // Don't check uniqueness if it's empty and required
    ) {
      // Clear manual uniqueness error before re-checking
      if (formState.errors.domain?.type === 'manual') {
        clearErrors('domain')
      }
      // Set initial state before async check
      setValue('unique_domain', false, { shouldValidate: true })
      void checkDomainUnique(dDomain, newController.signal)
    } else if (!dDomain || dDomain.trim().length === 0) {
      // If domain is cleared, reset uniqueness state and clear errors
      setValue('unique_domain', !isDomainRequired, { shouldValidate: true }) // If domain not required, it's "valid" even if empty
      clearErrors('domain')
      setCheckingDomain(false)
      newController.abort() // Abort any pending check
    } else {
      // If there's a format/required error, ensure unique_domain is false
      setValue('unique_domain', false, { shouldValidate: true })
      setCheckingDomain(false)
      newController.abort() // Abort any pending check
    }

    // Cleanup function to abort on unmount or dependency change
    return () => {
      newController.abort()
      setCheckingDomain(false)
    }
    // Only depend on the actual values that should trigger re-checking
    // Removed setValue, clearErrors, and formState.errors.domain to prevent loops
  }, [dDomain, checkUniqueDomain, isDomainRequired])

  // Change city options based on state.
  const watchState = watch('state')
  const stateOptions = US_STATES
  const cityOptions = useMemo(() => {
    const state = watchState
    if (!state) return []
    return US_CITIES.filter((city) => city.value.endsWith(state)).map(
      (city) => ({
        label: city.label.split(',')[0],
        value: city.label.split(',')[0],
      })
    )
  }, [watchState])

  // Change subcategory options based on category.
  const typeOptions = useMemo(() => {
    const category = watchCategory
    if (!category) return []
    const categoryObj = companyCategories?.find((c) => c.id === category)
    if (!categoryObj) return []
    return categoryObj.sub_categories
      .map((subCategory) => ({
        label: subCategory.name,
        value: subCategory.id,
        slug: subCategory.slug,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [watchCategory, companyCategories]) // Depend on companyCategories now

  const [dealsModal, setDealsModal] = useState(false)
  const [createdCompanyId, setCreatedCompanyId] = useState<number | null>(null)
  const _handleClose = (openDealsModal?: boolean) => {
    setShow(false)
    if (openDealsModal) {
      setDealsModal(true)
    }
  }

  const onSubmit = handleSubmit(
    async (values) => {
      // Ensure unique_domain is true before submitting if check is enabled and domain has value
      if (
        checkUniqueDomain &&
        values.domain &&
        values.domain.trim().length > 0 &&
        !values.unique_domain
      ) {
        setError('domain', { type: 'manual', message: 'Domain in use' })
        toast.error('Domain is already in use.')
        return // Prevent submission
      }

      const [err, res] = await to<Record<string, any> | undefined, AxiosError>(
        apiAction(values)
      )
      if (err?.response?.data) {
        const errors = err.response.data as { [key: string]: string[] }
        for (const [key, value] of Object.entries(errors)) {
          if (key === 'non_field_errors') {
            toast.error(value.join(', ')) // Join if multiple non-field errors
          } else if (key === 'unique_domain' && values.domain) {
            // Handle backend uniqueness validation specifically
            setError('domain', { message: value[0] })
            setValue('unique_domain', false) // Update frontend state
          } else if (key in companyFormShapeItems) {
            // Set error for known fields
            setError(key as keyof CompanyFormShape, {
              message: value[0] as any,
            })
          } else {
            // Handle unexpected errors
            toast.error(`Error saving company: ${value.join(', ')}`)
          }
        }
      }
      if (!err && res) {
        // Invalidate relevant queries
        await queryClient.invalidateQueries({
          queryKey: ['companyCategoriesAndSub'],
        })
        const subCategorySlug = companyCategories
          ?.find((c) => c.id === values.category)
          ?.sub_categories.find((s) => s.id === values.sub_category)?.slug
        if (subCategorySlug) {
          await queryClient.invalidateQueries({
            queryKey: [`${subCategorySlug}-table`],
          })
        } else {
          // Fallback invalidation if slug not found
          await queryClient.invalidateQueries({ queryKey: ['companies'] })
        }

        if (!skipDealCreation) {
          setCreatedCompanyId(res?.id)
        }
        // Reset form preserving initial default state for country/unique_domain
        reset({
          country: 'US',
          unique_domain: !checkUniqueDomain,
          ...defaultValues, // Re-apply original defaults if any
        })
        _handleClose(!skipDealCreation) // Open deals modal only if not skipped
      }
    },
    (errors) => {
      // Log validation errors for debugging
      console.error('Form validation errors:', errors)
      // Show toast for the first encountered error message
      const firstError = Object.values(errors)[0]
      if (firstError?.message) {
        toast.error(firstError.message)
      } else {
        toast.error('Please fix the errors in the form.')
      }
    }
  )

  const customCountryFilter = (option: SelectOption, rawInput: string) => {
    if (!rawInput) return true
    return (option.label as string)
      .toLowerCase()
      .startsWith(rawInput.toLowerCase())
  }

  const sortedCountries = useMemo(() => {
    return ALL_COUNTRIES.sort((a, b) =>
      a.value === 'US' ? -1 : a.label.localeCompare(b.label)
    )
  }, [])

  // Determine if address is required based on category/subcategory
  const isAddressRequired = useMemo(() => {
    const intermediariesCategory = companyCategories?.find(
      (c) => c.name === 'Intermediaries'
    )
    const otherSubCategory = intermediariesCategory?.sub_categories.find(
      (s) => s.name === 'Other'
    )
    const isIntermediariesOther =
      watchCategory === intermediariesCategory?.id &&
      watchSubCategory === otherSubCategory?.id
    return !isIntermediariesOther
  }, [watchCategory, watchSubCategory, companyCategories])

  const showCompanyTypeChangeWarning = useMemo(() => {
    return (
      defaultValues?.id &&
      (watchCategory !== defaultValues?.category ||
        watchSubCategory !== defaultValues?.sub_category)
    )
  }, [watchCategory, watchSubCategory, defaultValues])

  return (
    <>
      <Modal
        open={show}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            // Reset form state on close if needed, similar to _handleClose logic
            reset({
              country: 'US',
              unique_domain: !checkUniqueDomain,
              ...defaultValues,
            })
            controllerRef.current?.abort() // Abort any pending domain check
          }
          setShow(isOpen)
        }}
        title={title}
        description={description}
        acceptButtonText={acceptButtonText}
        onAccept={onSubmit}
        size={'lg'}
        loading={formState.isSubmitting || checkingDomain} // Show loading during submit or domain check
      >
        <TextInput
          noMargin={true}
          label={texts['nameLabel']}
          name="name"
          control={control}
          placeholder={texts['namePlaceholder']}
          disabled={disableFields?.includes('name')}
          // Required is handled by Zod
        />
        <div className={'relative'}>
          <TextInput
            label={texts['domainLabel']}
            name="domain"
            control={control}
            placeholder="company.com"
            disabled={disableFields?.includes('domain')}
            optional={!isDomainRequired} // Dynamically set optional prop for visual cue
          />
          {/* Show spinner while checking */}
          {checkingDomain && checkUniqueDomain && dDomain ? (
            <Spinner
              size={'sm'}
              className={'absolute bottom-2.5 right-2.5 z-10 text-muted'}
              animation="border"
              role="status"
              aria-hidden="true"
            />
          ) : // Show checkmark if check is enabled, domain has value, format is valid, and it's unique
          checkUniqueDomain &&
            watchDomain &&
            watchDomain.trim().length > 0 &&
            !formState.errors.domain && // No Zod errors (format/required)
            watchUniqueDomain ? (
            <BsCheck
              size={22}
              className={'absolute bottom-1.5 right-2 z-10 text-success'}
            />
          ) : // Show nothing or an error icon if applicable (e.g., !watchUniqueDomain && watchDomain)
          // Optional: Add an error icon if domain exists (!watchUniqueDomain)
          checkUniqueDomain &&
            watchDomain &&
            watchDomain.trim().length > 0 &&
            !formState.errors.domain && // No Zod errors
            !watchUniqueDomain ? (
            // Add an error icon component here if desired, e.g., <BsX className="text-danger ..."/>
            <></> // Placeholder for no icon
          ) : (
            <></> // Default: no icon
          )}
        </div>
        {!hideCategoryAndType && (
          <div>
            <div className={'flex gap-2 w-full max-w-full mt-3'}>
              <Select
                control={control}
                name="category"
                placeholder={'Category'}
                label={'Category'}
                options={categoryOptions}
                className={'flex-1'}
                disabled={disableFields?.includes('category')}
                // Required handled by Zod
              />
              <Select
                className={'flex-1'}
                control={control}
                name="sub_category"
                placeholder={'Type'}
                options={typeOptions}
                label={'Type'}
                disabled={
                  !watchCategory || disableFields?.includes('sub_category')
                }
                // Required handled by Zod
              />
            </div>
            {showCompanyTypeChangeWarning && (
              <ErrorLabel
                message="By changing this company's opportunity category and/or
                type, this company will be removed from any campaigns that are
                exclusive to its previous opportunity type."
              />
            )}
          </div>
        )}
        <div className={'flex gap-2 w-full max-w-full mt-3'}>
          <TextInput
            label="Google Maps URL"
            name="google_place_url"
            control={control}
            placeholder="Enter Google Place URL"
            disabled={disableFields?.includes('google_place_url')}
            optional // Always optional based on schema
          />
        </div>
        <div className={'flex gap-2 w-full max-w-full mt-3'}>
          <Select
            className={'flex-1'}
            control={control}
            name="country"
            placeholder={'Enter country'}
            options={sortedCountries}
            label={'Country'}
            filterOption={customCountryFilter}
            disabled={disableFields?.includes('country')}
            // Required handled by Zod
          />
        </div>
        <TextInput
          label="Address"
          helperText={texts['addressHelperText']}
          name="address"
          control={control}
          placeholder="Enter address"
          disabled={disableFields?.includes('address')}
          optional={!isAddressRequired} // Dynamically set optional prop
        />
        <div className={'flex gap-2 w-full max-w-full mt-3'}>
          {watchCountry === 'US' ? (
            <>
              <Select
                className={'flex-1 mt-0'}
                control={control}
                name="state"
                placeholder={'Enter state'}
                options={stateOptions}
                label={'State'}
                disabled={disableFields?.includes('state')}
                optional={false} // Required for US, handled by Zod
              />
              <CreatableSelect
                className={'flex-1 mt-0'}
                control={control}
                name="city"
                placeholder={'Enter city'}
                options={cityOptions}
                label={'City'}
                disabled={!watchState || disableFields?.includes('city')}
                onCreateOption={async (city) => {
                  return {
                    label: city,
                    value: city,
                  }
                }}
                // Required handled by Zod
              />
            </>
          ) : (
            <>
              <TextInput
                noMargin
                label="State/Province" // More generic label
                name="state"
                control={control}
                placeholder="Enter state/province"
                disabled={disableFields?.includes('state')}
                optional // Optional for non-US, handled by Zod
              />
              <TextInput
                noMargin
                label="City"
                name="city"
                control={control}
                placeholder="Enter city"
                disabled={disableFields?.includes('city')}
                // Required handled by Zod
              />
            </>
          )}
        </div>
        <div className={'flex gap-2 w-full max-w-full mt-3'}>
          <TextInput
            label="ZIP/Postal code" // More generic label
            name="zipcode"
            control={control}
            placeholder="Enter ZIP/Postal code"
            disabled={disableFields?.includes('zipcode')}
            optional={!isAddressRequired}
            // Required handled by Zod
          />
        </div>
        <PhoneInput
          name="phone_number"
          extensionName="extension"
          control={control}
          label="Phone number"
          optional // Always optional based on schema
        />
        {!hideKnownDistributorsAndTags && (
          <div className={'flex flex-col sm:flex-row gap-2 w-full pb-4'}>
            <Select
              style={{ flex: 1, flexShrink: 0 }}
              className={'mt-3 sm:mt-0'}
              name="distributors"
              isMulti
              control={control}
              type="text"
              disabled={disableFields?.includes('distributors')}
              options={distributorsOptions}
              placeholder="Known Distributor(s)"
              label={'Known Distributor(s)'}
              optional
            />
            <Select
              style={{ flex: 1, flexShrink: 0 }}
              className={'mt-3 sm:mt-0'}
              name="taglist"
              isMulti
              control={control}
              type="text"
              disabled={disableFields?.includes('taglist')}
              options={tagsOptions}
              placeholder="Tag(s)"
              label={'Tag(s)'}
              optional
            />
          </div>
        )}
      </Modal>
      <CreateDealModal
        totalSelectedRows={1}
        optimisticUiTarget="create-company"
        cancelButtonText={'Skip deal creation'}
        show={dealsModal}
        handleClose={() => setDealsModal(false)}
        targetCompanyId={createdCompanyId ?? undefined}
        tableTypeSlug="other"
        companyTypeSlug="other"
      />
    </>
  )
}
